import React, { useState } from "react";
import moment from "moment";

import { Button, Typography } from "@material-ui/core";

import {
  TableColumnConfiguration,
  TablePaginationOptions
} from "../../../components/layout/table/types";
import Table from "../../../components/layout/table";
import List from "../../../components/layout/list";
import {
  DiscountAmountType,
  Receipt,
  ReceiptDiscountItem,
  ReceiptItem
} from "../../../models/User";

import ReceiptDetailsModal from "../modals/ReceiptDetailsModal";

import { ReceiptsTableProps } from "./types";
import useStyles from "./styles";
import { Currency } from "../../../components/misc/Currency";
import { useSelector } from "react-redux";
import { AppState } from "../../../store";
import { Market } from "../../../models/Market";
import { splitCamelCase } from "../../../utils";
import { renderProductName } from "../../../models/Product";
import styles from "../styles";
import { useI18n } from "../../../hooks/useI18n";

const ReceiptsCollection = ({
  screenSize,
  receipts,
  onRefund
}: ReceiptsTableProps) => {
  const styles = useStyles();
  const { t } = useI18n();

  const [currentPage, setCurrentPage] = useState<number>(0);

  const [selectedReceipt, setSelectedReceipt] = useState<Receipt>({
    userId: "",
    id: 0,
    currency: "",
    store: { id: "", name: "" },
    netAmount: 0,
    grossAmount: 0,
    createdDate: new Date(),
    items: []
  });

  const [showReceiptDetailsModal, setShowReceiptDetailsModal] =
    useState<boolean>(false);

  const rowsPerPage = screenSize === "desktop" ? 10 : 5;

  const paginationOptions: TablePaginationOptions = {
    count: receipts.length,
    page: currentPage,
    onPageChange: (event, page) => setCurrentPage(page),
    rowsPerPage
  };

  const renderProductName = (item: ReceiptItem): string => {
    return `${item.brandName ?? ""} ${item.productName} ${item.size}`.trim();
  };

  const mainTableColumnConfiguration: TableColumnConfiguration<Receipt>[] = [
    {
      name: t("common.tableHeaders.receiptNr"),
      renderItem: receipt => receipt.id
    },
    {
      name: "Store",
      renderItem: receipt => receipt.store.name
    },
    {
      name: "Amount",
      renderItem: receipt => (
        <Currency value={receipt.netAmount} currency={receipt.currency} />
      )
    },
    {
      name: "Created",
      renderItem: receipt => (
        <span>{moment(receipt.createdDate).format("YYYY-MM-DD HH:mm")}</span>
      )
    },
    {
      name: t("common.tableHeaders.paymentMethod"),
      renderItem: receipt =>
        receipt.paymentMethodType && splitCamelCase(receipt.paymentMethodType)
    },
    {
      name: "",
      renderItem: receipt => (
        <Button
          color="secondary"
          variant="outlined"
          size="small"
          disabled={receipt.netAmount <= 0}
          onClick={() => onRefund(receipt.id)}
        >
          Refund
        </Button>
      )
    },
    {
      name: "",
      renderItem: receipt => (
        <Button
          color="secondary"
          variant="outlined"
          size="small"
          onClick={() => {
            window.open(
              `/v1/users/${receipt.userId}/download-receipt?receiptId=${receipt.id}`
            );
          }}
        >
          Download
        </Button>
      )
    }
  ];

  const innerTableColumnConfiguration = (
    receipt: Receipt
  ): TableColumnConfiguration<ReceiptItem>[] => [
    {
      name: "Product",
      renderItem: receiptItem => renderProductName(receiptItem)
    },
    {
      name: "Price",
      renderItem: receiptItem => {
        if (receiptItem.depositFee) {
          return (
            <>
              <Currency value={receiptItem.price} />
              <Typography color="textSecondary" variant="body2" component={"p"}>
                incl. deposit fee (
                <Currency
                  value={receiptItem.depositFee}
                  currency={receipt.currency}
                />
                )
              </Typography>
            </>
          );
        }
        return (
          <Currency value={receiptItem.price} currency={receipt.currency} />
        );
      }
    },
    {
      name: t("common.tableHeaders.discountedPrice"),
      renderItem: receiptItem => receiptItem?.discountedPrice
    },
    {
      name: "Quantity",
      renderItem: receiptItem => receiptItem.quantity
    },
    {
      name: t("common.tableHeaders.refundQuantity"),
      renderItem: receiptItem => receiptItem.refundQuantity
    },
    {
      name: t("common.formFields.campaignName"),
      renderItem: receiptItem => receiptItem.campaignName
    },
    {
      name: "Total",
      renderItem: receiptItem => (
        <Currency value={receiptItem.itemAmount} currency={receipt.currency} />
      )
    }
  ];

  const renderReceiptListItem = (receipt: Receipt) => (
    <div className={styles.receiptListItem}>
      <div className={styles.receiptItemHeader}>
        <span className={styles.receiptItemDate}>
          {moment(receipt.createdDate).format("YYYY-MM-DD HH:mm")}
        </span>
        <span className={styles.receiptItemAmount}>
          {receipt.id} (
          <Currency
            value={receipt.netAmount - calculateDiscount(receipt)}
            currency={receipt.currency}
          />
          )
        </span>
      </div>

      <div className={styles.receiptItemButtonContainer}>
        <Button
          color="primary"
          variant="contained"
          size="small"
          onClick={() => {
            setSelectedReceipt(receipt);
            setShowReceiptDetailsModal(true);
          }}
        >
          Details
        </Button>

        <Button
          color="secondary"
          variant="outlined"
          size="small"
          disabled={receipt.netAmount <= 0}
          onClick={() => onRefund(receipt.id)}
        >
          Refund
        </Button>
      </div>
    </div>
  );

  const calculateDiscount = (receipt: Receipt) => {
    if (!receipt.discountItem) {
      return 0;
    }

    return receipt.discountItem.amountType === DiscountAmountType.Percentage
      ? receipt.netAmount * receipt.discountItem.amount
      : receipt.discountItem.amount;
  };

  const paginationStartIndex = currentPage * rowsPerPage;
  const paginationEndIndex = Math.min(
    (currentPage + 1) * rowsPerPage,
    receipts.length
  );

  const formatDiscount = (discount: ReceiptDiscountItem) => {
    if (discount.amountType === DiscountAmountType.Percentage) {
      return `-${discount.amount * 100}%`;
    }

    return <Currency value={-discount.amount} />;
  };

  return screenSize === "desktop" ? (
    <Table
      columns={mainTableColumnConfiguration}
      data={receipts.slice(paginationStartIndex, paginationEndIndex)}
      keyExtractor={receipt => receipt.id}
      expandedRowRender={receipt => (
        <>
          <Table
            columns={innerTableColumnConfiguration(receipt)}
            data={receipt.items}
            keyExtractor={receiptItem => `${receipt.id}_${receiptItem.id}`}
          />

          {receipt.discountItem && (
            <div className={styles.discountItem}>
              Discount applied: {formatDiscount(receipt.discountItem)}
            </div>
          )}
        </>
      )}
      paginationOptions={paginationOptions}
    />
  ) : (
    <>
      <List
        items={receipts.slice(paginationStartIndex, paginationEndIndex)}
        renderItem={renderReceiptListItem}
        keyExtractor={receipt => receipt.id}
        paginationOptions={paginationOptions}
      />

      <ReceiptDetailsModal
        receipt={selectedReceipt}
        visible={showReceiptDetailsModal}
        onOk={() => setShowReceiptDetailsModal(false)}
        onCancel={() => {}}
      />
    </>
  );
};

export default ReceiptsCollection;
